import React, { useCallback, useContext, useMemo } from "react";
import clsx from "clsx";

import {
	HasPermissions,
	Button,
	TableEditButtons,
	OnlineLink,
} from "components";
import { permissionList } from "helpers";
import { appStatuses } from "mappers";

import ApplicationListContext from "./applicationListContext";

import { ReactComponent as RemoveIcon } from "static/images/cancel.svg";
import { ReactComponent as CopyIcon } from "static/images/copy_icon.svg";
import { ReactComponent as NotesIcon } from "static/images/dialog.svg";
import { ReactComponent as PencilEditIcon } from "static/images/pencil_edit.svg";

const ApplicationListActions = ({
	row,
	setRowState,
	showInlineEditButton,
	showRemoveButton,
}) => {
	const { copyDocument, removeApplication, showAddNoteModal } = useContext(
		ApplicationListContext,
	);

	const {
		id: applicationId,
		can_be_copied,
		can_be_edited,
		application_status,
		is_paper_application,
		number,
	} = row?.original || {};

	const isPaperApplication = is_paper_application;

	const editPath = useMemo(
		() =>
			can_be_edited && isPaperApplication
				? `/applications/upload-mpa/${applicationId}/general-info`
				: can_be_edited
				? `/applications/edit/${applicationId}`
				: "#",
		[can_be_edited, isPaperApplication, applicationId],
	);

	const handleCopy = useCallback(
		e => {
			e.stopPropagation();
			copyDocument(applicationId, isPaperApplication);
		},
		[copyDocument, applicationId, isPaperApplication],
	);

	const showAddDealNoteModal = e => {
		e.stopPropagation();
		showAddNoteModal({ entityId: applicationId });
	};

	const cabBeInlineEdited = [
		appStatuses.UPLOADED,
		appStatuses.UNSIGNED_CREATED_BY_MERCHANT,
		appStatuses.UNSIGNED_PENDING_COMPLETION,
		appStatuses.UNSIGNED_PRE_APP_LINK_SENT,
	];

	const isEditButtonDisabled =
		!cabBeInlineEdited.includes(application_status);

	const canBeRemoved = [
		appStatuses.UNSIGNED_CREATED_BY_MERCHANT,
		appStatuses.UPLOADED,
		appStatuses.UNSIGNED_PENDING_COMPLETION,
		appStatuses.UNSIGNED_PRE_APP_LINK_SENT,
	];
	const isRemoveButtonDisabled = !canBeRemoved.includes(application_status);

	return (
		<div className="d-flex align-items-center">
			<HasPermissions
				requiredPermissions={permissionList.StartNewApplication}
			>
				{showInlineEditButton && (
					<TableEditButtons
						setEditMode={edit => setRowState?.(row.id, { edit })}
						isEditMode={row?.state.edit}
						disabledEditButton={isEditButtonDisabled}
					/>
				)}
				<Button
					btnIcon
					icon={<PencilEditIcon />}
					onClick={e => e.stopPropagation()}
					as={OnlineLink}
					to={editPath}
					variant="secondary"
					size="sm"
					title="Edit"
					disabled={!can_be_edited}
					className={clsx(!can_be_edited && "disabled")}
				/>
				<Button
					btnIcon
					icon={<CopyIcon />}
					onClick={handleCopy}
					variant="secondary"
					size="sm"
					title="Copy"
					disabled={!can_be_copied}
				/>
			</HasPermissions>
			<HasPermissions
				requiredPermissions={permissionList.AddAccountNotes}
			>
				<Button
					btnIcon
					icon={<NotesIcon />}
					onClick={showAddDealNoteModal}
					variant="secondary"
					size="sm"
					title="Add deal note"
				/>
			</HasPermissions>
			{showRemoveButton && (
				<HasPermissions
					requiredPermissions={permissionList.RemoveDraftApplication}
				>
					<Button
						onClick={e => {
							e.stopPropagation();
							removeApplication(applicationId, number);
						}}
						size="sm"
						icon={<RemoveIcon />}
						btnIcon
						variant="secondary"
						title="Remove"
						disabled={isRemoveButtonDisabled}
					/>
				</HasPermissions>
			)}
		</div>
	);
};

export default ApplicationListActions;
